import { useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { Icon } from '../../components/Icon';
import { useI18n } from '../../i18n/i18n-context';
import { rich } from '../../i18n/rich';
import { motionAllowed } from '../../lib/motion';
import { PromoPlayer, type PromoPlayerHandle } from '../promo/PromoPlayer';
import { FeatureArt } from './FeatureArt';
import { clock, type FeaturePromo } from '../promo/promos';

/** Preserve the animated preview until the writer chooses to watch this feature's film. */
export function HomeFeature({
  promo,
  index,
}: {
  readonly promo: FeaturePromo;
  readonly index: number;
}) {
  const { t } = useI18n();
  const feature = t.home.features[promo.id];
  const [opened, setOpened] = useState(false);
  const player = useRef<PromoPlayerHandle>(null);
  const preview = useRef<HTMLDivElement>(null);
  const toggle = useRef<HTMLButtonElement>(null);
  const watchLabel = `${t.promo.play} · ${feature.label}`;

  useEffect(() => {
    const art = preview.current;
    if (!art || typeof IntersectionObserver === 'undefined' || !motionAllowed()) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          entry.target.toggleAttribute('data-in-view', entry.isIntersecting);
        }
      },
      { threshold: 0.3 },
    );
    observer.observe(art);
    return () => {
      observer.disconnect();
    };
  }, [opened]);

  function close() {
    setOpened(false);
    toggle.current?.focus({ preventScroll: true });
  }

  return (
    <article
      className={`home-feature home-feature--${promo.id} home-reveal`}
      aria-labelledby={`home-feature-${promo.id}`}
      onKeyDown={(event) => {
        if (event.key === 'Escape' && opened && !document.fullscreenElement) {
          event.stopPropagation();
          close();
        }
      }}
    >
      <div
        className={`home-feature-media${opened ? ' home-feature-media--playing' : ''}`}
        id={`home-demo-${promo.id}`}
      >
        {opened ? (
          <div className="home-feature-film">
            <PromoPlayer
              ref={player}
              src={promo.src}
              poster={promo.poster}
              captions={promo.captions}
              label={feature.title}
              watchLabel={watchLabel}
              initialDuration={promo.duration}
              preload="none"
            />
          </div>
        ) : (
          <div className="home-feature-art" ref={preview} aria-hidden="true">
            <FeatureArt feature={promo.id} />
          </div>
        )}
      </div>
      <div className="home-feature-text">
        <p className="home-feature-kicker">
          <span className="home-feature-number">{String(index + 1).padStart(2, '0')}</span>
          {feature.label}
        </p>
        <h3 id={`home-feature-${promo.id}`}>{feature.title}</h3>
        <p className="home-feature-body">
          {promo.id === 'rewrite'
            ? rich(feature.body, {
                diff: <strong>{t.card.diff}</strong>,
                why: <strong>{t.card.why}</strong>,
                alts: <strong>{t.card.alts}</strong>,
              })
            : feature.body}
        </p>
        <button
          ref={toggle}
          type="button"
          className="home-feature-watch"
          aria-label={opened ? `${t.common.back} · ${feature.label}` : watchLabel}
          aria-expanded={opened}
          aria-controls={`home-demo-${promo.id}`}
          onClick={() => {
            if (opened) {
              close();
              return;
            }
            // Mount and start within the click gesture, including on browsers that restrict autoplay.
            flushSync(() => {
              setOpened(true);
            });
            player.current?.playFrom(0);
          }}
        >
          <Icon name={opened ? 'close' : 'play'} />
          <span>{opened ? t.common.back : t.promo.play}</span>
          {!opened && <span dir="ltr">{clock(promo.duration)}</span>}
        </button>
      </div>
    </article>
  );
}
